import { createSlice } from '@reduxjs/toolkit';
import { current } from '@reduxjs/toolkit';

import {
  getAllContacts,
  getAllMessages,
  sendMessage,
  startChat,
  sendReaction,
  blockChatUser,
  resetUnreadCount,
  unblockChatUser,
  getBlockedUsers,
  initiateCall,
  receiveCall,
  terminateCall,
} from './sales-support-action';
import { DEFAULT_SALES_SUPPORT_STATE } from '../../../constants/defaultStates/sales-support';

const salesSupportSlice = createSlice({
  name: 'sales_support',
  initialState: DEFAULT_SALES_SUPPORT_STATE,
  reducers: {
    ChangeAgentChatStatus(state, action) {
      state.agentChatStatus = action.payload;
    },
    changeAllotedAgent(state, action) {
      state.allotedAgent = action.payload;
    },
    changeUser(state, action) {
      const incomingUser = action.payload || {};
      const existingUser =
        state.user?.phone && state.user.phone === incomingUser?.phone ? state.user : null;

      state.user = {
        ...incomingUser,
        ...(typeof incomingUser?.isOptOut === 'boolean'
          ? {}
          : existingUser && typeof existingUser?.isOptOut === 'boolean'
            ? { isOptOut: existingUser.isOptOut }
            : {}),
      };
      // state.chatId = state.allotedAgent.mob + "-" + action.payload.phone;
      state.chatId = incomingUser?.phone || null;
    },
    resetChatUser(state) {
      state.user = DEFAULT_SALES_SUPPORT_STATE.user;
      state.chatId = DEFAULT_SALES_SUPPORT_STATE.chatId;
    },
    setImage(state, action) {
      state.image = action.payload;
    },
    setFiles(state, action) {
      state.files = action.payload;
    },
    updateFiles(state, action) {
      state.files = state.files?.map((item) => {
        if (item.id == action.payload.id) return action.payload;
        else return item;
      });
    },
    showFilelimitWarning(state, action) {
      state.filesLength = action.payload.filesLength;
      state.filesLengthWarning = action.payload.filesLengthWarning;
    },
    deleteFiles(state, action) {
      state.files = state.files.filter((x) => x?.id !== action.payload);
      if (state.files.length === 0) {
        state.isFileSelect = false;
      }
    },
    setIsImageSelected(state, action) {
      state.isImageSelect = action.payload;
    },
    setIsFileSelect(state, action) {
      state.isFileSelect = action.payload;
    },
    setVideo(state, action) {
      state.video = action.payload;
    },
    setDocument(state, action) {
      state.document = action.payload;
    },
    setFileSending(state, action) {
      state.fileSending = action.payload;
    },
    clearfiles(state) {
      state.files = DEFAULT_SALES_SUPPORT_STATE.files;
    },
    setIsVideoSelected(state, action) {
      state.isVideoSelect = action.payload;
    },
    setIsProductSelected(state, action) {
      state.isProductSelect = action.payload;
    },
    changeAgentSearchtUser(state, action) {
      state.agentSearchItem = action.payload?.toLowerCase();
    },
    changeForwardModalAgentSearchtUser(state, action) {
      state.forwardModalAgentSearchItem = action.payload?.toLowerCase();
    },
    changeIsUserSelected(state, action) {
      state.isUserSelected = action.payload;
    },
    changeTicketStatusFilter(state, action) {
      state.ticketStatusFilter = action.payload;
    },
    changeAgentFilter(state, action) {
      state.selectedAgent = action.payload;
    },
    changeForwardModalTicketStatusFilter(state, action) {
      state.forwardModalTicketStatusFilter = action.payload;
    },
    setlockUpload(state, action) {
      state.uploadlock = action.payload;
    },
    changeTicketID(state, action) {
      state.user.ticketId = action.payload;
    },
    updateUserStatus(state, { payload }) {
      state.user = { ...state.user, ...payload };
    },
    switchShowQuickNote(state) {
      state.showQuickNotes = !state.showQuickNotes;
    },
    changeIsRecAudioSelect(state, action) {
      state.isRecAudioSelect = action.payload;
    },

    setForwardMessage(state, action) {
      state.forwardMessagesFlag = action.payload.forwardMessagesFlag;
      state.forwardMessages = action.payload.forwardMessages;
      state.selectedForwardMessagesContent = action.payload.selectedForwardMessagesContent;
    },
    clearForwardMessage(state) {
      state.forwardMessagesFlag = false;
      state.forwardMessages = {};
      state.selectedForwardMessagesContent = [];
      state.clickedForwardFirstMessageId = null;
    },
    setForwardFirstMessageId(state, action) {
      state.clickedForwardFirstMessageId = action.payload.clickedForwardFirstMessageId;
    },
    clearForwardFirstMessageId(state, action) {
      state.clickedForwardFirstMessageId = null;
    },
    setReplyMessage(state, action) {
      state.replyMessage = action?.payload?.replyMessage;
      state.messageForReply = action?.payload?.messageForReply;
      state.repliedMessageFlag = action?.payload?.repliedMessageFlag;
    },
    clearReplyMessage(state) {
      state.replyMessage = false;
      state.messageForReply = {};
    },
    clearRepliedMessageFlag(state) {
      state.repliedMessageFlag = false;
    },
    updateContactFromSocket: (state, action) => {
      const updatedContact = action.payload;

      // Find existing contact
      const index = state.contacts.findIndex((c) => c.phone === updatedContact.phone);

      if (index !== -1) {
        // Update contact data
        state.contacts[index] = {
          ...state.contacts[index],
          ...updatedContact,
          ...(typeof updatedContact?.isOptOut === 'boolean'
            ? {}
            : { isOptOut: state.contacts[index]?.isOptOut }),
        };

        // Move updated contact to top
        const moved = state.contacts[index];
        state.contacts.splice(index, 1); // remove from old position
        state.contacts.unshift(moved); // add to top
      } else {
        // If contact not found → push new contact
        state.contacts.unshift(updatedContact);
      }
    },
  },
  extraReducers: (builder) => {
    // Get All Contacts
    builder.addCase(getAllContacts.fulfilled, (state, { payload, meta }) => {
      const pageNo = meta?.arg?.pageNo || 0;

      if (pageNo > 0) {
        state.contacts = [...state.contacts, ...payload.rows];
      } else {
        state.contacts = payload.rows;
      }

      const limit = meta?.arg?.limit || 30;
      state.hasMore = payload.rows.length === limit;
      state.loading = false;
    });
    builder.addCase(getAllContacts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllContacts.rejected, (state) => {
      state.loading = false;
    });

    // Get All Messages
    builder.addCase(getAllMessages.fulfilled, (state, { payload }) => {
      state.messages = payload?.rows;
      state.loading = false;
    });
    builder.addCase(getAllMessages.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllMessages.rejected, (state, { payload }) => {
      state.loading = false;
    });

    // Send Message
    builder.addCase(sendMessage.fulfilled, (state, { payload }) => {
      state.loading = false;
    });
    builder.addCase(sendMessage.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(sendMessage.rejected, (state, { payload }) => {
      state.loading = false;
    });

    // Start Chat
    builder.addCase(startChat.fulfilled, (state, { payload }) => {
      state.loading = false;
    });
    builder.addCase(startChat.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(startChat.rejected, (state, { payload }) => {
      state.loading = false;
    });

    // Send Reaction
    builder.addCase(sendReaction.fulfilled, (state, { payload }) => {
      state.loading = false;
    });
    builder.addCase(sendReaction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(sendReaction.rejected, (state, { payload }) => {
      state.loading = false;
    });

    // Reset Unread Count
    builder.addCase(resetUnreadCount.fulfilled, (state, { payload }) => {
      if (payload?.data?.id) {
        const contactIndex = state.contacts.findIndex((c) => c.id === payload.data.id);

        if (contactIndex !== -1) {
          // ✅ Update unread count and timestamps (or any other fields)
          state.contacts[contactIndex].unreadMessages = payload.data.unreadMessages;
        }
      }
      state.loading = false;
    });
    builder.addCase(resetUnreadCount.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(resetUnreadCount.rejected, (state, { payload }) => {
      state.loading = false;
    });

    const updateBlockedContactState = (state, payload) => {
      const isBlocked = payload.blockStatus === 'block';

      if (state.user?.phone === payload.phone) {
        state.user = {
          ...state.user,
          isOptOut: isBlocked,
        };
      }

      state.contacts = state.contacts.map((contact) =>
        contact.phone === payload.phone ? { ...contact, isOptOut: isBlocked } : contact,
      );
      state.loading = false;
    };

    builder.addCase(blockChatUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(blockChatUser.fulfilled, (state, { payload }) => {
      updateBlockedContactState(state, payload);
    });
    builder.addCase(blockChatUser.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(unblockChatUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(unblockChatUser.fulfilled, (state, { payload }) => {
      updateBlockedContactState(state, payload);
    });
    builder.addCase(unblockChatUser.rejected, (state) => {
      state.loading = false;
    });
    // ================= CALLS =================
    builder.addCase(initiateCall.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(initiateCall.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.callStatus = 'initiated';
      state.activeCall = payload?.data || payload;
    });
    builder.addCase(initiateCall.rejected, (state) => {
      state.loading = false;
      state.callStatus = 'failed';
    });
    builder.addCase(receiveCall.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(receiveCall.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.callStatus = 'connected';
      state.activeCall = payload?.data || payload;
    });
    builder.addCase(receiveCall.rejected, (state) => {
      state.loading = false;
      state.callStatus = 'failed';
    });
    builder.addCase(terminateCall.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(terminateCall.fulfilled, (state) => {
      state.loading = false;
      state.callStatus = 'terminated';
      state.activeCall = null;
    });
    builder.addCase(terminateCall.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getBlockedUsers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBlockedUsers.fulfilled, (state, { payload }) => {
      const { data } = payload || {};
      state.loading = false;
      state.blockedUsers = data || [];
      // state.blockedUsersCursor = paging.cursors || state.blockedUsersCursor;
    });
    builder.addCase(getBlockedUsers.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const {
  changeUser,
  setDocument,
  setFileSending,
  clearfiles,
  setIsFileSelect,
  setIsImageSelected,
  setIsProductSelected,
  setVideo,
  setIsVideoSelected,
  setImage,
  setFiles,
  showFilelimitWarning,
  deleteFiles,
  setlockUpload,
  changeAllotedAgent,
  changeAgentSearchtUser,
  changeForwardModalAgentSearchtUser,
  changeIsUserSelected,
  changeTicketStatusFilter,
  changeForwardModalTicketStatusFilter,
  updateUserStatus,
  changeTicketID,
  resetChatUser,
  switchShowQuickNote,
  changeIsRecAudioSelect,
  setForwardMessage,
  clearForwardMessage,
  setForwardFirstMessageId,
  clearForwardFirstMessageId,
  setReplyMessage,
  clearReplyMessage,
  clearRepliedMessageFlag,
  changeAgentFilter,
  updateFiles,
  ChangeAgentChatStatus,
  updateContactFromSocket,
} = salesSupportSlice.actions;

export default salesSupportSlice.reducer;
